import React from 'react';
import cs from 'classnames';
import { Investment } from '@tcs/client-portal-api/types/Investment';
import { palette, Typography } from '@forgeglobal/design-framework';
import Table from '@tcs/client-portal-common/components/molecules/Table';
import BaseHeader from '@tcs/client-portal-common/components/molecules/Table/cells/headers/BaseHeader';
import InvestmentValueCell from './InvestmentValueCell';
import ColorCell from '@tcs/client-portal-common/components/molecules/Table/cells/ColorCell';
import DecimalAsPercentageCell from '@tcs/client-portal-common/components/molecules/Table/cells/DecimalAsPercentageCell';
import TwoLineCell from '@tcs/client-portal-common/components/molecules/Table/cells/TwoLineCell';
import { doesInvestmentHaveUnits } from '@tcs/client-portal-common/utils/doesInvestmentHaveUnits';
import BaseCell from '@tcs/client-portal-common/components/molecules/Table/cells/BaseCell';
import ExpandCell from '@tcs/client-portal-common/components/molecules/Table/cells/ExpandCell';
import { flexRender } from '@tanstack/react-table';
import TitleCellHeader from '@tcs/client-portal-common/components/molecules/Table/cells/headers/TitleCellHeader';
import ViewportControlCell from '@tcs/client-portal-common/components/molecules/Table/cells/ViewportControlCell';
import TitleCell from '@tcs/client-portal-common/components/molecules/Table/cells/TitleCell';
import DepthControlCell from '@tcs/client-portal-common/components/molecules/Table/cells/DepthControlCell';
import { useStyles } from './style';
import { useStyles as useTableStyles } from '@tcs/client-portal-common/components/molecules/Table/style';
import { getAssetTypeLabel } from '@tcs/client-portal-common/utils/getAssetTypeLabel';

interface Props {
  cashBalance: Investment;
  investmentBalances: Investment[];
  onRowClick: (investment: Investment) => void;
}

const hiddenAssetCodes = new Set(['CASH']);
const childrenColumns = ['assetCode', 'numberOfUnits'];

const AccountHoldingsTable = ({
  cashBalance,
  investmentBalances,
  onRowClick,
}: Props) => {
  const classes = useStyles(undefined);
  const tableClasses = useTableStyles({});

  const cashColor = palette.dataVisualizationColors.ForestGreen;
  const assetColors = Object.values(palette.dataVisualizationColors).filter(
    (c) => c !== palette.dataVisualizationColors.ForestGreen
  );

  const investmentRows = investmentBalances.map((holding, index) => {
    const color = assetColors[index];
    const numberOfUnits = doesInvestmentHaveUnits(holding)
      ? holding.totalUnits
      : undefined;
    return { ...holding, color, numberOfUnits };
  });

  const formattedInvestmentRows = Object.values(
    investmentRows.reduce((acc, item) => {
      const { assetType, assetValueType } = item;

      const label = getAssetTypeLabel(assetType, assetValueType);

      return {
        ...acc,
        [label]: {
          assetType: label,
          color: item.color,
          details: [...(acc[label]?.details || []), item],
          investmentValue:
            (acc[label]?.investmentValue || 0) + item.investmentValue,
          investmentAllocation:
            (acc[label]?.investmentAllocation || 0) + item.investmentAllocation,
        },
      };
    }, {})
  );

  // cash row shouldn't be grouped
  const accountHoldingRows = [
    { ...cashBalance, color: cashColor },
    ...formattedInvestmentRows,
  ];

  const columns = [
    {
      accessor: 'color',
      header: () => null,
      cell: (info) => (
        <ColorCell
          color={info.getValue()}
          expanded={info.row.getIsExpanded()}
          secondary={info.row.depth > 0}
        />
      ),
      size: 30,
    },
    {
      accessor: 'assetDescription',
      header: () => <TitleCellHeader text="Asset Type" />,
      cell: (info) => {
        if (!info.row.depth && !info.row.getCanExpand()) {
          return <BaseCell value={info.getValue()} />;
        }
        const code = info.row.original.assetCode;
        return (
          <DepthControlCell
            parent={
              <BaseCell
                value={`${info.row.original.assetType} ${
                  !!info.row.subRows.length
                    ? `(${info.row.subRows.length})`
                    : ''
                }`}
              />
            }
            child={
              <ViewportControlCell
                mobile={<TitleCell text={info.getValue()} />}
                desktop={
                  <TwoLineCell
                    primary={info.getValue()}
                    secondary={!hiddenAssetCodes.has(code) && code}
                  />
                }
              />
            }
            isChild={!!info.row.depth}
          />
        );
      },
      size: 300,
    },
    {
      accessor: 'assetCode',
      header: () => (
        <ViewportControlCell mobile={<BaseHeader text="ID" />} desktop={null} />
      ),
      cell: (info) => (
        <DepthControlCell
          parent={null}
          child={
            <ViewportControlCell
              mobile={<BaseCell value={info.getValue()} />}
              desktop={null}
            />
          }
          isChild={!!info.row.depth}
        />
      ),
      size: 10,
    },
    {
      accessor: 'investmentAllocation',
      header: () => <BaseHeader text="Investment Allocation	" align="right" />,
      cell: (info) => <DecimalAsPercentageCell value={info.getValue()} />,
      size: 150,
    },
    {
      accessor: 'numberOfUnits',
      header: () => <BaseHeader text="# of Units" align="right" />,
      cell: (info) => (
        <DepthControlCell
          parent={
            <ViewportControlCell
              mobile={null}
              desktop={
                <BaseCell
                  value={info.getValue()?.toLocaleString()}
                  align="left"
                  isPrivate
                />
              }
            />
          }
          child={
            <BaseCell
              value={info.getValue()?.toLocaleString()}
              align="left"
              isPrivate
            />
          }
          isChild={!!info.row.depth}
        />
      ),
      size: 150,
    },
    {
      accessor: 'investmentValue',
      header: () => <BaseHeader text="Investment Value" align="right" />,
      cell: (info) => (
        <InvestmentValueCell
          value={info.getValue()}
          numberOfUnits={info.row.original.numberOfUnits}
        />
      ),
      size: 200,
    },
  ];

  const mobileTableLayout = (table, onRowClick) => {
    return (
      <div className={tableClasses.cardTable}>
        <div className={tableClasses.cardTableName}>
          <Typography variant="body2">Asset Type</Typography>
        </div>
        {table.getRowModel().rows.map((row) => {
          return (
            <div className={tableClasses.cardRowWrapper} key={row.id}>
              {row.getVisibleCells().map((cell, index) => {
                if (index !== 0) return null;
                return (
                  <div key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                );
              })}
              <div
                className={cs(tableClasses.cardRow, classes.tableRow)}
                onClick={onRowClick(row)}>
                {row.getVisibleCells().map((cell, index) => {
                  // hide color cell
                  if (index === 0) return null;
                  // hide unneeded labels for parent rows
                  if (!row.depth && childrenColumns.includes(cell.column.id))
                    return null;

                  return (
                    <div className={tableClasses.cardCell} key={cell.id}>
                      {(!row.getCanExpand() || !!row.depth) && (
                        <div className={tableClasses.cardTitle}>
                          {flexRender(
                            cell.column.columnDef.header,
                            table
                              .getHeaderGroups()[0]
                              .headers[index].getContext()
                          )}
                        </div>
                      )}
                      <div
                        className={cs(tableClasses.cardValue, {
                          [classes.textSecondary]: !row.depth && index > 2,
                        })}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {table.getCanSomeRowsExpand() && (
                <div className={tableClasses.cardExpand}>
                  <ExpandCell
                    isExpanded={row.getIsExpanded()}
                    canExpand={row.getCanExpand()}
                    onToggle={row.getToggleExpandedHandler()}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <Typography
        variant="h5"
        color="textPrimary"
        className={classes.title}
        data-test-id="table-title">
        Account Holdings
      </Typography>
      <Table
        data={accountHoldingRows}
        columns={columns}
        onRowClick={onRowClick}
        customMobileLayout={mobileTableLayout}
      />
    </div>
  );
};

export default AccountHoldingsTable;
